import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { TrendingUp, TrendingDown, Receipt, ArrowRight } from "lucide-react";

const BUCKET_META = [
  { key: "material",      label: "Material Cost",      color: "#0EA5E9", hint: "Panels, inverters, cables, structures, freight" },
  { key: "labour",        label: "Labour Cost",        color: "#F59E0B", hint: "Installation crews, engineering, T&C, project team" },
  { key: "documentation", label: "Documentation Cost", color: "#8B5CF6", hint: "KSEB, net metering, handover materials" },
  { key: "other",         label: "Other Cost",         color: "#6B7280", hint: "Warranty, service, contingency, consumables" },
];

export default function ProjectProfitabilityCard({ projectId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const load = () => api.get(`/projects/${projectId}/profitability`)
    .then((r) => { setData(r.data); setErr(false); })
    .catch(() => { setData(null); setErr(true); });

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("finance:refresh", h);
    return () => window.removeEventListener("finance:refresh", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (err) return null;
  if (!data) return null;

  const isProfit = data.gross_margin >= 0;
  const marginColor = isProfit ? "#10B981" : "#DC2626";

  return (
    <div className="flat-card" data-testid="project-profitability-card">
      <div className="px-6 py-4 border-b border-[#E5E7EB] flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 text-[#002FA7]" />
          <div className="label-eyebrow">Project Profitability</div>
          <span className="text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 bg-[#FEF3C7] text-[#92400E]">Admin Only</span>
        </div>
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#4B5563]" data-testid="profit-expense-count">
          {data.expense_count} vendor entr{data.expense_count === 1 ? "y" : "ies"}
        </span>
      </div>

      <div className="p-6 space-y-5">
        {/* Top: Contract & Gross Margin */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="border border-[#E5E7EB] p-4">
            <div className="label-eyebrow text-[9px]">Contract Value</div>
            <div className="text-2xl font-black tracking-tight mt-1" data-testid="profit-contract">{inr(data.contract_value)}</div>
            <div className="text-[10px] text-[#9CA3AF] mt-1">Revenue · what the customer signed</div>
          </div>
          <div className="border-2 p-4" style={{ borderColor: marginColor }}>
            <div className="label-eyebrow text-[9px] flex items-center gap-1">
              {isProfit ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              Gross Margin
            </div>
            <div className="text-2xl font-black tracking-tight mt-1" style={{ color: marginColor }} data-testid="profit-gross-margin">
              {inr(data.gross_margin)}
            </div>
            <div className="text-[10px] uppercase tracking-widest font-bold mt-1" style={{ color: marginColor }} data-testid="profit-margin-pct">
              {data.margin_pct}% of contract
            </div>
          </div>
        </div>

        {/* Math line */}
        <div className="text-[11px] text-[#4B5563] font-mono bg-[#F9FAFB] border border-[#E5E7EB] px-3 py-2">
          {inr(data.contract_value)} − {inr(data.material_cost)} − {inr(data.labour_cost)} − {inr(data.documentation_cost)} − {inr(data.other_cost)} = <b style={{ color: marginColor }}>{inr(data.gross_margin)}</b>
        </div>

        {/* Buckets — clickable */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {BUCKET_META.map((b) => {
            const amount = Number(data[`${b.key}_cost`] || 0);
            const typesParam = (data.buckets?.[b.key] || []).join(",");
            const href = `/finance?tab=expenses&project_id=${projectId}&expense_types=${encodeURIComponent(typesParam)}`;
            return (
              <Link
                key={b.key}
                to={href}
                className="border border-[#E5E7EB] p-3 hover:border-[#0a0a0a] hover:bg-[#FAFAFA] transition-colors block group"
                data-testid={`profit-bucket-${b.key}`}
              >
                <div className="flex items-center justify-between">
                  <span className="label-eyebrow text-[9px]" style={{ color: b.color }}>{b.label}</span>
                  <ArrowRight className="w-3 h-3 text-[#9CA3AF] group-hover:text-[#0a0a0a] transition-colors" />
                </div>
                <div className="text-lg font-black tracking-tight mt-1 text-[#DC2626]">
                  {amount > 0 ? `− ${inr(amount)}` : inr(0)}
                </div>
                <div className="text-[10px] text-[#9CA3AF] mt-1 leading-tight">{b.hint}</div>
              </Link>
            );
          })}
        </div>

        <div className="text-[10px] text-[#9CA3AF] flex items-center gap-1">
          Each tile opens the <Link to="/finance" className="underline">Finance · Expenses</Link> view pre-filtered for this project + bucket.
        </div>
      </div>
    </div>
  );
}
